import { useState } from "react";
import { AlertCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";

import { Modal, Show } from "shared/components";
import { useAuthContext } from "providers/Auth";
import { DecisionAction } from "shared/types";
import { Application } from "types/application";
import { FullBreakdown } from "./components/FullBreakdown";
import { Header } from "./components/Header";
import { ModalFooter } from "./components/ModalFooter";
import { ReasonsSection } from "./components/Reasons";
import { SummaryConditions } from "./components/SummaryConditions";
import { WhatToReview } from "./components/WhatToReview";
import { useRecommendation } from "./hooks/use-recommendation";

interface ExplainRecommendationModalProps {
  application: Application;
  opened: boolean;
  onClose: () => void;
}

export const ExplainRecommendationModal = ({
  application,
  opened,
  onClose
}: ExplainRecommendationModalProps) => {
  const [isFullBreakdownVisible, setIsFullBreakdownVisible] = useState(false);
  const { permissions } = useAuthContext();

  const {
    currentRecommendation,
    recommendationMeta,
    summaryRules,
    surfacedRules,
    isMetDeclineConditions,
    isMetApprovalConditions
  } = useRecommendation(application);

  return (
    <Modal
      opened={opened && permissions.canAccessExplainRecommendationModal}
      onClose={onClose}
      panelClassName="overflow-hidden max-h-[90vh] bg-content-1000-inverted max-w-[1002px] w-full animate-appear border border-alpha-100 shadow-lg"
    >
      <Header
        onClose={onClose}
        recommendationTitle={recommendationMeta.title}
        recommendationVariant={recommendationMeta.badgeVariant}
      />

      <div className="w-full flex flex-col max-h-[calc(90vh-160px)] overflow-auto">
        <div className="w-full">
          <div className="p-6 bg-background-900">
            <ReasonsSection
              surfacedRules={surfacedRules}
              isDecline={currentRecommendation === DecisionAction.Decline}
              recommendationDetails={
                recommendationMeta.details ? (
                  <>
                    <HugeiconsIcon
                      size={20}
                      strokeWidth={1.5}
                      className="text-content-300"
                      icon={AlertCircleIcon}
                    />
                    {recommendationMeta.details}
                  </>
                ) : null
              }
            />

            <SummaryConditions
              subtitle={recommendationMeta.subtitle}
              currentRecommendation={currentRecommendation}
              summaryRules={summaryRules}
              applicants={application.applicants}
              isMetDeclineConditions={isMetDeclineConditions}
              isMetApprovalConditions={isMetApprovalConditions}
            />

            <Show
              if={
                currentRecommendation === DecisionAction.ConditionalApprove &&
                surfacedRules.length > 0
              }
            >
              <WhatToReview
                surfacedRules={surfacedRules}
                applicants={application.applicants}
              />
            </Show>
          </div>
        </div>

        <ModalFooter
          isFullBreakdownVisible={isFullBreakdownVisible}
          onToggleFullBreakdown={() =>
            setIsFullBreakdownVisible((isVisible) => !isVisible)
          }
          onClose={onClose}
        />

        <Show if={isFullBreakdownVisible}>
          <FullBreakdown
            application={application}
            isMetApprovalConditions={isMetApprovalConditions}
            isMetDeclineConditions={isMetDeclineConditions}
          />
        </Show>
      </div>
    </Modal>
  );
};
